import React from "react";
import { CancellationRequestCard } from "../cards/CancellationRequestCard";
import { MessageLayout } from "../layout/MessageLayout";
import { useChatActions } from "../../context/ChatActionContext";
import type { ChatMessageProps } from "../types";
import type { CancellationRequestMessageResponseDto } from "@semo/api-contracts";
import { CancellationRequestDataDtoStatus } from "@semo/api-contracts";
import type { LocalMessageState } from "../../types/message";

interface CancellationRequestMessageProps extends Omit<
  ChatMessageProps,
  "message"
> {
  message: CancellationRequestMessageResponseDto & LocalMessageState;
  sender: "me" | "other";
  time: string;
}

export const CancellationRequestMessage: React.FC<
  CancellationRequestMessageProps
> = ({
  message,
  messageId,
  role,
  sender,
  partyName,
  time,
  showAvatar = true,
  showName = true,
}) => {
  const { isRead, createdAt, isPending, isError } = message;
  const { onContractAction, onContractReject, onRetry, onPrintReceipt } =
    useChatActions();

  if (!message.cancellationRequestData) return null;

  // Define local type intersection to include tid (which is in metadata but not yet in DTO)
  type CancellationRequestDataWithTid =
    typeof message.cancellationRequestData & {
      tid?: string;
      paymentMethod?: "CARD" | "CASH";
      requesterRole?: "buyer" | "supplier";
    };
  const data =
    message.cancellationRequestData as unknown as CancellationRequestDataWithTid;

  return (
    <MessageLayout
      sender={sender}
      partyName={partyName}
      time={time}
      isRead={isRead}
      isPending={isPending}
      isError={isError}
      showAvatar={showAvatar}
      showName={showName}
      variant="card"
      onRetry={() => onRetry?.(message.id)}
    >
      <CancellationRequestCard
        message={{ createdAt }}
        reason={data.requestReason || ""}
        rejectionReason={data.rejectionReason || undefined}
        status={data.status || CancellationRequestDataDtoStatus.pending}
        role={role}
        requesterRole={data.requesterRole}
        sender={sender}
        tid={data.tid}
        paymentMethod={data.paymentMethod}
        onApprove={() => onContractAction?.(messageId!)}
        onReject={() => onContractReject?.(messageId!)}
        onPrintReceipt={
          onPrintReceipt ? () => onPrintReceipt(messageId!) : undefined
        }
      />
    </MessageLayout>
  );
};
